/* ============================================================================
   Asequibilidad. La cifra de la canasta contra el ingreso que existe para pagarla.

   Cuidado metodológico que esta vista hace explícito: la canasta se publica por
   PERSONA y el salario mínimo por TRABAJADOR. Compararlos exige declarar cuántas
   personas dependen de ese salario, y ese supuesto es del usuario, no nuestro.
   Por eso el tamaño del hogar es un control visible y no una constante escondida.

   Tampoco mezclamos dos escalas en un eje: cuando hay dos medidas distintas, van
   en dos gráficas. Nunca un doble eje Y.
   ========================================================================== */

import { useMemo, useState } from 'react'
import { LineChart } from '../charts/LineChart'
import { Segmented } from '../components/Segmented'
import { StatTile } from '../components/StatTile'
import { Callout, Card, FilterBar, FilterField, Section, Select } from '../components/ui'
import {
  AMBITO_COLOR,
  AMBITO_LABEL,
  AMBITO_LABEL_F,
  AMBITO_OPTIONS,
  DEFAULT_HOUSEHOLD_SIZE,
  SERIES,
  canastaAlimentaria,
  canastaAmpliada,
  series,
  type Ambito,
} from '../data/dataset'
import {
  DAYS_PER_MONTH,
  RANGE_OPTIONS,
  affordabilitySeries,
  availableRanges,
  changeOver,
  sliceRange,
  type RangeKey,
} from '../data/derive'
import { currency0, formatPeriodTitle, number, percent } from '../lib/format'

/**
 * Frase completa del cambio, no sólo el verbo: si redondea a cero el resultado es
 * «no cambió» a secas. Devolver sólo el verbo producía «no cambió 0.0 %», que
 * suena a que sí cambió esa cantidad.
 */
function movimiento(percentValue: number): string {
  const rounded = Number(percentValue.toFixed(1))
  if (rounded === 0) return 'no cambió'
  return `${rounded > 0 ? 'subió' : 'bajó'} ${percent(Math.abs(rounded))}`
}

const PEOPLE_OPTIONS = [1, 2, 3, 4, 5, 6].map((n) => ({
  value: String(n),
  label: n === 1 ? '1 persona' : `${n} personas`,
}))

export function Asequibilidad({
  ambito,
  onAmbitoChange,
}: {
  ambito: Ambito
  onAmbitoChange: (value: Ambito) => void
}) {
  const [people, setPeople] = useState(String(DEFAULT_HOUSEHOLD_SIZE))
  const [metric, setMetric] = useState<'canastas' | 'porcentaje'>('canastas')
  /* Arranca en «Todo». La serie cubre 1992-2026 y el propósito de esta pantalla
     es justamente la trayectoria larga: con la ventana de cinco años que había
     antes, el peor momento de los 34 años —diciembre de 2014— quedaba fuera de
     cuadro y la recuperación reciente se leía como si fuera toda la historia. */
  const [range, setRange] = useState<RangeKey>('all')

  const alimentaria = series(canastaAlimentaria(ambito))
  const ampliada = series(canastaAmpliada(ambito))
  const salario = series(SERIES.salarioMinimoDiario)
  const householdSize = Number(people)

  const rows = useMemo(
    () => affordabilitySeries(alimentaria, salario, householdSize),
    [alimentaria, salario, householdSize],
  )

  const ranges = useMemo(
    () => RANGE_OPTIONS.filter((option) => availableRanges(salario.points).includes(option.value)),
    [salario.points],
  )

  const latest = rows[rows.length - 1] ?? null
  const first = rows[0] ?? null

  const basketsSeries = useMemo(
    () =>
      rows.map((row) => ({
        period: row.period,
        value: metric === 'canastas' ? row.baskets : row.shareOfWage,
      })),
    [rows, metric],
  )

  const wageMonthly = useMemo(
    () =>
      sliceRange(salario.points, range).map((point) => ({
        period: point.period,
        value: point.value * DAYS_PER_MONTH,
      })),
    [salario.points, range],
  )

  const householdBasket = useMemo(
    () =>
      sliceRange(alimentaria.points, range)
        .filter((point) => point.period >= (salario.points[0]?.period ?? '1900-01'))
        .map((point) => ({ period: point.period, value: point.value * householdSize })),
    [alimentaria.points, householdSize, salario.points, range],
  )

  const householdAmpliada = useMemo(
    () =>
      sliceRange(ampliada.points, range)
        .filter((point) => point.period >= (salario.points[0]?.period ?? '1900-01'))
        .map((point) => ({ period: point.period, value: point.value * householdSize })),
    [ampliada.points, householdSize, salario.points, range],
  )

  /* Comparar por índice de arreglo supone que los meses son consecutivos y sin
     huecos. Se compara por PERIODO: `changeOver` busca el punto de hace doce
     meses por su fecha y devuelve null si no existe. */
  const basketsChange = useMemo(() => {
    const asPoints = rows.map((row) => ({
      period: row.period,
      value: row.baskets,
      sourceId: 'derivado',
      confidence: 'medium' as const,
    }))
    return changeOver(asPoints, 12)?.percent ?? null
  }, [rows])

  const wageChange = useMemo(() => changeOver(salario.points, 12), [salario.points])

  const annualBasket = changeOver(alimentaria.points, 12)

  /* La canasta ampliada tiene que ser la del MISMO mes que el salario con el que
     se compara; tomar el último punto de cada serie cruzaba meses distintos si
     una de las dos iba retrasada. Sin coincidencia no hay cifra: null, no cero. */
  const latestAmpliada =
    latest === null ? undefined : ampliada.points.find((p) => p.period === latest.period)
  const ampliadaHousehold = latestAmpliada ? latestAmpliada.value * householdSize : null
  const ampliadaBaskets =
    latest !== null && ampliadaHousehold !== null && ampliadaHousehold > 0
      ? latest.monthlyWage / ampliadaHousehold
      : null

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Asequibilidad</p>
        <h1 className="t-title1">El costo de comer contra el ingreso que hay para pagarlo</h1>
        <p className="t-body ink-2">
          La canasta se publica por persona; el salario mínimo, por trabajador. Para compararlos hay
          que decir cuántas personas dependen de ese salario. Ese supuesto es el control de abajo, no
          una constante escondida en el código.
        </p>
      </header>

      <FilterBar ariaLabel="Supuestos de asequibilidad">
        <FilterField label="Ámbito">
          <Segmented
            options={AMBITO_OPTIONS}
            value={ambito}
            onChange={onAmbitoChange}
            ariaLabel="Ámbito geográfico" />
        </FilterField>
        <FilterField label="Personas por salario mínimo">
          <Select value={people} onChange={setPeople} options={PEOPLE_OPTIONS} ariaLabel="Personas" />
        </FilterField>
        <FilterField label="Indicador">
          <Segmented
            options={[
              { value: 'canastas', label: 'Canastas' },
              { value: 'porcentaje', label: '% del salario' },
            ]}
            value={metric}
            onChange={setMetric}
            ariaLabel="Indicador de asequibilidad" />
        </FilterField>
        <FilterField label="Periodo">
          <Segmented options={ranges} value={range} onChange={setRange} ariaLabel="Periodo" />
        </FilterField>
      </FilterBar>

      <div className="grid grid--kpi">
        <StatTile
          label={`Canastas alimentarias de ${householdSize} ${householdSize === 1 ? 'persona' : 'personas'} que compra un salario mínimo`}
          value={latest?.baskets ?? 0}
          format={(value) => number(value, 2)}
          delta={basketsChange ?? undefined}
          deltaSince="vs. hace un año"
          invertDelta
          trend={rows.slice(-24).map((row) => row.baskets)}
          accent={AMBITO_COLOR[ambito]}
          emphasis
          note={
            /* Tres casos, no dos: «y sólo para eso» sólo es defendible pegado a
               1. Con un hogar de una persona el mismo salario compra casi cuatro
               canastas, y la coletilla contradecía al número de arriba. */
            latest === null
              ? 'Sin dato.'
              : latest.baskets < 1
                ? 'Un salario mínimo no alcanza para la comida de este hogar.'
                : latest.baskets < 1.15
                  ? 'Un salario mínimo alcanza para la comida de este hogar, y prácticamente sólo para eso.'
                  : 'Un salario mínimo alcanza para la comida de este hogar.'
          }
        />
        <StatTile
          label="La comida del hogar como porcentaje del salario mínimo"
          value={latest?.shareOfWage ?? 0}
          format={(value) => percent(value, 1)}
          invertDelta={false}
          note={
            latest
              ? `Canasta familiar de ${currency0(latest.householdBasket)} contra un salario mensual de ${currency0(latest.monthlyWage)}.`
              : 'Sin dato.'
          }
        />
        <StatTile
          label="Salario mínimo mensual vigente"
          value={latest?.monthlyWage ?? 0}
          format={(value) => currency0(value)}
          note={`Zona de salarios mínimos generales. ${DAYS_PER_MONTH.toFixed(4)} días al mes, la mensualización oficial de CONASAMI.`}
        />
        <StatTile
          label="Canastas alimentarias más no alimentarias que compra un salario mínimo"
          value={ampliadaBaskets ?? 0}
          format={(value) => (ampliadaBaskets === null ? 'sin dato' : number(value, 2))}
          invertDelta
          note={
            ampliadaBaskets === null
              ? 'No hay canasta alimentaria más no alimentaria publicada para el mismo mes del salario.'
              : 'Incluye no alimentos: vivienda, transporte, salud, educación.'
          }
        />
      </div>

      <Section
        title={
          metric === 'canastas'
            ? 'Cuántas canastas familiares compra un salario mínimo'
            : 'Cuánto del salario mínimo cuesta la comida del hogar'
        }
        description={
          metric === 'canastas'
            ? 'Por arriba de 1 el salario alcanza para la comida del hogar. Aquí subir es favorable.'
            : 'Por arriba de 100 % la comida del hogar cuesta más que el salario completo. Aquí subir es adverso.'
        }
      >
        <Card>
          <LineChart
            series={[
              {
                id: 'asequibilidad',
                label:
                  metric === 'canastas'
                    ? 'Canastas alimentarias por salario mínimo'
                    : 'Porcentaje del salario en comida',
                shortLabel: metric === 'canastas' ? 'Canastas' : '% del salario',
                color: AMBITO_COLOR[ambito],
                points: basketsSeries,
              },
            ]}
            title={
              metric === 'canastas'
                ? `Canastas de ${householdSize} ${householdSize === 1 ? 'persona' : 'personas'} por salario mínimo`
                : 'La canasta alimentaria del hogar como porcentaje del salario mínimo'
            }
            subtitle={
              first
                ? `Desde ${formatPeriodTitle(first.period).toLowerCase()}: es el primer mes con salario mínimo en la serie. La canasta se publica desde 1992, pero sin salario no hay comparación.`
                : undefined
            }
            formatValue={(value) =>
              metric === 'canastas' ? number(value, 2) : percent(value, 0)
            }
            unitLabel={metric === 'canastas' ? 'canastas' : 'por ciento del salario'}
            height={300}
            hRules={
              metric === 'canastas'
                ? [{ value: 1, label: 'Una canasta' }]
                : [{ value: 100, label: 'Todo el salario' }]
            }
            zeroBased
            area
            footnote="El salario mínimo cambia una sola vez al año, el 1 de enero: los escalones de la serie son reales, no un artefacto."
          />
        </Card>
      </Section>

      <Section
        title="Las dos cifras por separado"
        description="Dos medidas distintas nunca comparten un eje. Aquí las dos están en pesos al mes, así que sí caben en una sola gráfica y la comparación es honesta."
      >
        <Card>
          <LineChart
            series={[
              {
                id: 'salario',
                label: 'Salario mínimo mensual',
                shortLabel: 'Salario mínimo',
                color: 'var(--series-3)',
                points: wageMonthly,
              },
              {
                id: 'canasta',
                label: `Canasta alimentaria de ${householdSize} ${householdSize === 1 ? 'persona' : 'personas'}`,
                shortLabel: 'Canasta alimentaria',
                color: AMBITO_COLOR[ambito],
                points: householdBasket,
              },
              {
                id: 'ampliada',
                label: `Canasta alimentaria más no alimentaria de ${householdSize} ${householdSize === 1 ? 'persona' : 'personas'}`,
                /* El nombre oficial no cabe al final de la curva; el completo va en
                   `label`, que es lo que leen la leyenda y la tabla espejo. */
                shortLabel: 'Con no alimentos',
                color: 'var(--series-7)',
                points: householdAmpliada,
              },
            ]}
            title="Salario mínimo y canastas familiares, en pesos al mes"
            subtitle={`Últimos cinco años, ámbito ${AMBITO_LABEL[ambito]}, hogar de ${householdSize} ${householdSize === 1 ? 'integrante' : 'integrantes'}.`}
            formatValue={(value) => currency0(value)}
            unitLabel="pesos al mes"
            height={320}
            zeroBased
          />
        </Card>
      </Section>

      <Callout kind="warning" title="Los supuestos de esta comparación">
        <p>
          Un hogar rara vez vive de un solo salario mínimo, y muchos hogares no reciben ni siquiera
          eso: el salario mínimo es un piso legal, no el ingreso observado. Esta vista no dice cuántos
          hogares están en esa situación; dice cuánto alcanza ese piso.
        </p>
        <p>
          El salario mínimo se mensualiza con {DAYS_PER_MONTH.toFixed(4)} días (365/12), la convención
          de CONASAMI. La canasta se mensualiza con 30 días, la convención del INEGI. Son cifras
          oficiales de cada institución con su propio criterio; la diferencia es de aproximadamente{' '}
          {percent(((DAYS_PER_MONTH - 30) / 30) * 100, 1)} y va a favor del salario.
        </p>
        <p>
          Hasta septiembre de 2015 no existía «el» salario mínimo: había tres áreas geográficas con
          montos distintos. Para ese tramo se usa el promedio nacional que publica la propia CONASAMI,
          ponderado con la población asalariada; desde octubre de 2015 las áreas se unificaron y el
          monto ya es único. Los montos de 1992 están convertidos a nuevos pesos, porque el decreto de
          junio de 1992 quitó tres ceros a partir del año siguiente.
        </p>
        <p>
          La composición de la canasta es la misma en toda la serie —la de la ENIGH 2016, actualizada
          mes a mes con el Índice Nacional de Precios al Consumidor—, así que la comparación mide una
          sola cosa a lo largo de {new Date().getFullYear() - 1992} años: cuánto de esa canasta
          alcanzaba a comprar el piso legal del salario en cada momento.
        </p>
        <p>
          {/* El verbo se elige por el signo: «subió» delante de un porcentaje
              negativo decía lo contrario del dato. */}
          {annualBasket && wageChange
            ? `En el último año la canasta alimentaria ${AMBITO_LABEL_F[ambito]} ${movimiento(annualBasket.percent)} y el salario mínimo general ${movimiento(wageChange.percent)}.`
            : ''}
        </p>
      </Callout>
    </>
  )
}
